import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertOctagon,
  AlertTriangle,
  ArrowUpRight,
  BellOff,
  Check,
  Cloud,
  Info,
  Lock,
  Mail,
  MailOpen,
  ShieldAlert,
  Unlock,
  X,
  type LucideIcon,
} from "lucide-react";
import type {
  AdminNotification,
  NotificationCategory,
  NotificationSeverity,
} from "./Api/adminNotificationApi";
import type { EndpointSeverityAlert } from "./useEndpointSeverityAlerts";

interface NotificationsPanelContentProps {
  notifications: AdminNotification[];
  loaded: boolean;
  sessionNewIds: Set<string>;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onLock: (id: string) => void;
  onUnlock: (id: string) => void;
  onRemove: (id: string) => void;
  onViewDetails: (notification: AdminNotification) => void;
  endpointAlerts?: EndpointSeverityAlert[];
  onViewEndpointAlerts?: () => void;
  emptyText?: string;
}

const SEVERITY_ICONS: Record<NotificationSeverity, LucideIcon> = {
  CRITICAL: AlertOctagon,
  HIGH: ShieldAlert,
  MEDIUM: AlertTriangle,
  LOW: Info,
  INFO: Info,
};

const SEVERITY_LABELS: Record<NotificationSeverity, string> = {
  CRITICAL: "Critical",
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
  INFO: "Info",
};

const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  CLOUD_ALERT: "Cloud alert",
  SYSTEM: "System",
  VERSION_UPDATE: "Update",
  GENERAL: "General",
  RESOURCE_REQUEST: "Resource request",
};

const MAX_SECTION_DAY = 7;

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function sectionLabel(daysAgo: number): string {
  if (daysAgo <= 0) return "Latest";
  if (daysAgo === 1) return "Yesterday";
  return `${daysAgo} days ago`;
}

function formatTime(createdAt: Date, daysAgo: number, now: number): string {
  if (daysAgo <= 0) {
    const minutes = Math.floor((now - createdAt.getTime()) / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  }
  return createdAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function normalizeSeverity(value?: string | null): NotificationSeverity | null {
  const key = value?.trim().toUpperCase();
  if (key === "CRITICAL" || key === "HIGH" || key === "MEDIUM" || key === "LOW" || key === "INFO") return key;
  return null;
}

function NotificationItem({
  notification,
  isNew,
  isEntering,
  timeLabel,
  onMarkRead,
  onMarkUnread,
  onLock,
  onUnlock,
  onRemove,
  onViewDetails,
}: {
  notification: AdminNotification;
  isNew: boolean;
  isEntering: boolean;
  timeLabel: string;
} & Omit<NotificationsPanelContentProps, "notifications" | "loaded" | "sessionNewIds" | "emptyText">) {
  const severity = normalizeSeverity(notification.severity);
  const SeverityIcon = severity
    ? SEVERITY_ICONS[severity]
    : notification.category === "RESOURCE_REQUEST" ? Mail : Info;
  const locked = notification.status === "LOCKED";
  const expired = notification.status === "EXPIRED";
  const read = notification.status === "READ" || locked || expired;
  const cloudLabel = [notification.cloudType, notification.cloudAccountId].filter(Boolean).join(" · ");

  return (
    <li
      className="nt-item"
      data-severity={severity ?? "NONE"}
      data-category={notification.category}
      data-read={read}
      data-new={isNew}
      data-entering={isEntering}
    >
      <span className="nt-severity-icon" aria-hidden="true">
        <SeverityIcon />
      </span>

      <div className="nt-body">
        <div className="nt-title-row">
          {isNew && <span className="nt-new-dot" aria-label="New" />}
          <span className="nt-title" title={notification.title}>{notification.title}</span>
          {locked && (
            <span className="nt-chip" data-tone="primary">
              <Lock aria-hidden="true" />
              Locked
            </span>
          )}
          {expired && <span className="nt-chip" data-tone="muted">Expired</span>}
        </div>
        {notification.details && <p className="nt-details">{notification.details}</p>}
        <div className="nt-meta">
          <span className="nt-time">{timeLabel}</span>
          {severity && (
            <span className="nt-chip" data-tone="severity">{SEVERITY_LABELS[severity]}</span>
          )}
          <span className="nt-chip" data-tone="muted">{CATEGORY_LABELS[notification.category] ?? notification.category}</span>
          {cloudLabel && (
            <span className="nt-meta-item">
              <Cloud aria-hidden="true" />
              {cloudLabel}
            </span>
          )}
          {notification.tenantName && <span className="nt-meta-item">{notification.tenantName}</span>}
        </div>
      </div>

      <div className="nt-actions">
        <button
          type="button"
          className="nt-details-button"
          onClick={() => onViewDetails(notification)}
        >
          View details
          <ArrowUpRight aria-hidden="true" />
        </button>
        {!locked && !expired && (
          read ? (
            <button
              type="button"
              className="nt-icon-button"
              onClick={() => onMarkUnread(notification.id)}
              title="Mark as unread"
              aria-label="Mark as unread"
            >
              <MailOpen />
            </button>
          ) : (
            <button
              type="button"
              className="nt-icon-button"
              data-tone="success"
              onClick={() => onMarkRead(notification.id)}
              title="Mark as read"
              aria-label="Mark as read"
            >
              <Check />
            </button>
          )
        )}
        <button
          type="button"
          className="nt-icon-button"
          data-tone="primary"
          data-active={locked}
          onClick={() => (locked ? onUnlock(notification.id) : onLock(notification.id))}
          title={locked ? "Unlock" : "Lock"}
          aria-label={locked ? "Unlock" : "Lock"}
        >
          {locked ? <Unlock /> : <Lock />}
        </button>
        <button
          type="button"
          className="nt-icon-button"
          data-tone="danger"
          onClick={() => onRemove(notification.id)}
          disabled={locked}
          title={locked ? "Unlock to remove" : "Remove"}
          aria-label="Remove"
        >
          <X />
        </button>
      </div>
    </li>
  );
}

function EndpointAlertItem({
  alert,
  onView,
}: {
  alert: EndpointSeverityAlert;
  onView: () => void;
}) {
  const SeverityIcon = SEVERITY_ICONS[alert.severity];

  return (
    <li className="nt-item" data-severity={alert.severity} data-read="true">
      <span className="nt-severity-icon" aria-hidden="true">
        <SeverityIcon />
      </span>
      <div className="nt-body">
        <div className="nt-title-row">
          <span className="nt-title" title={alert.title}>{alert.title}</span>
        </div>
        {alert.details && <p className="nt-details">{alert.details}</p>}
        <div className="nt-meta">
          {alert.timeLabel && <span className="nt-time">{alert.timeLabel}</span>}
          <span className="nt-chip" data-tone="severity">{SEVERITY_LABELS[alert.severity]}</span>
        </div>
      </div>
      <div className="nt-actions">
        <button type="button" className="nt-details-button" onClick={onView}>
          View alerts
          <ArrowUpRight aria-hidden="true" />
        </button>
      </div>
    </li>
  );
}

function EndpointAlertsSection({
  alerts,
  onView,
}: {
  alerts: EndpointSeverityAlert[];
  onView: () => void;
}) {
  const criticalCount = alerts.filter((alert) => alert.severity === "CRITICAL").length;
  const highCount = alerts.filter((alert) => alert.severity === "HIGH").length;

  return (
    <section className="nt-section">
      <h3 className="nt-section-title">
        Critical & high
        <span className="nt-section-count">{alerts.length}</span>
      </h3>
      <p className="nt-severity-breakdown">
        <span data-severity="CRITICAL">{criticalCount} critical</span>
        <span data-severity="HIGH">{highCount} high</span>
      </p>
      <ul className="nt-list">
        {alerts.slice(0, 8).map((alert) => (
          <EndpointAlertItem key={alert.id} alert={alert} onView={onView} />
        ))}
      </ul>
    </section>
  );
}

export default function NotificationsPanelContent({
  notifications,
  loaded,
  sessionNewIds,
  endpointAlerts = [],
  onViewEndpointAlerts,
  emptyText = "No notifications in the last 7 days.",
  ...actions
}: NotificationsPanelContentProps) {
  const knownIdsRef = useRef<Set<string> | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const sections = useMemo(() => {
    const today = startOfDay(new Date());
    const groups = new Map<number, { daysAgo: number; items: { notification: AdminNotification; timeLabel: string }[] }>();
    notifications.forEach((notification) => {
      const createdAt = new Date(notification.createdAt);
      const daysAgo = Math.min(
        MAX_SECTION_DAY,
        Math.max(0, Math.round((today - startOfDay(createdAt)) / 86400000)),
      );
      if (!groups.has(daysAgo)) groups.set(daysAgo, { daysAgo, items: [] });
      groups.get(daysAgo)!.items.push({ notification, timeLabel: formatTime(createdAt, daysAgo, now) });
    });
    return Array.from(groups.values()).sort((a, b) => a.daysAgo - b.daysAgo);
  }, [notifications, now]);

  const knownIds = knownIdsRef.current;
  useEffect(() => {
    if (!loaded) return;
    knownIdsRef.current = new Set(notifications.map((notification) => notification.id));
  }, [loaded, notifications]);

  if (!loaded) {
    return (
      <div className="nt-scroll" aria-busy="true">
        <ul className="nt-list">
          {[0, 1, 2, 3].map((index) => (
            <li key={index} className="nt-item nt-item-skeleton">
              <span className="nt-skeleton nt-skeleton-icon" />
              <div className="nt-body">
                <span className="nt-skeleton nt-skeleton-title" />
                <span className="nt-skeleton nt-skeleton-text" />
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (notifications.length === 0 && endpointAlerts.length === 0) {
    return (
      <div className="nt-empty">
        <span className="nt-empty-icon" aria-hidden="true">
          <BellOff />
        </span>
        <p className="nt-empty-title">You're all caught up</p>
        <p className="nt-empty-text">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="nt-scroll">
      {endpointAlerts.length > 0 && onViewEndpointAlerts && (
        <EndpointAlertsSection alerts={endpointAlerts} onView={onViewEndpointAlerts} />
      )}
      {sections.map((section) => (
        <section key={section.daysAgo} className="nt-section">
          <h3 className="nt-section-title">
            {sectionLabel(section.daysAgo)}
            <span className="nt-section-count">{section.items.length}</span>
          </h3>
          <ul className="nt-list">
            {section.items.map(({ notification, timeLabel }) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                timeLabel={timeLabel}
                isNew={notification.status === "UNSEEN" || (sessionNewIds.has(notification.id) && notification.status === "SEEN")}
                isEntering={knownIds !== null && !knownIds.has(notification.id)}
                {...actions}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
